import { useEffect, useState } from 'react';
import {
  Box,
  CardActionArea as MuiCardActionArea,
  CardMedia as MuiCardMedia,
  Checkbox,
  Divider,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import dayjs from 'dayjs';
import type { StorageUnit } from 'store/storageunits/types';
import { StorageUnitStatus, StorageUnitType } from 'store/storageunits/types';
import relativeTime from 'dayjs/plugin/relativeTime';
import { splitName } from 'util/nameHelper';
import updateLocale from 'dayjs/plugin/updateLocale';
import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as SharedIcon } from 'icons/shared.svg';
import { ReactComponent as ApprovedIcon } from 'icons/approvedFiles.svg';
import { ReactComponent as TemplateIcon } from 'icons/templateMenu.svg';
import { isShared } from 'util/isSharedHelper';
import { useStorageUnits } from 'contexts/storageUnitContext';
import { CardTitle } from './styles';
import Card from './Card';
import MoreButton from './MoreButton';
import {
  isLeasedByAnotherUser,
  isLeasedByCurrentUser,
} from '../../utils/isLeasedByAnotherUser';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Chip from 'components/Chip';
import { useSelection } from '../../context';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import OfflineStatusIndicator from './OfflineStatusIndicator';
import { hasOfflineFileData } from 'store/storageunits/utils';

dayjs.extend(updateLocale);
dayjs.extend(relativeTime);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: 'a minute',
    mm: '%dm',
    h: 'an hour',
    hh: '%dh',
    d: 'a day',
    dd: '%dd',
    M: 'a month',
    MM: '%dm',
    y: 'a year',
    yy: '%dy',
  },
});

type Props = {
  storageUnit: StorageUnit;
};

const CardActionArea = styled(MuiCardActionArea)`
  padding: 0 ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(1)};
`;

const CardMedia = styled(MuiCardMedia)`
  overflow: hidden;
  border-radius: 2px;
  position: relative;
  max-height: 127px;
`;

const Thumbnail = styled('img')`
  width: 100%;
  height: 127px;
  object-fit: scale-down;
  background: ${({ theme }) => theme.palette.grey[50]};
  pointer-events: none;
`;

const ThumbnailPlaceholder = styled('div')`
  width: 100%;
  height: 127px;
  background: ${({ theme }) => theme.palette.grey[50]};
`;

const LastEdited = styled(Typography)`
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.palette.grey[700]};
`;

const Info = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  max-height: 40px;
  box-sizing: border-box;
`;

const StyledSharedIcon = styled(SharedIcon)`
  height: 24px;
  width: 24px;
  color: ${({ theme }) => theme.palette.grey[800]};
`;

const CardHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(1)}
    0;
`;

const StatusChip = ({ status }: { status: StorageUnitStatus[] }) => {
  const { palette } = useTheme();
  const isApproved = status.includes(StorageUnitStatus.APPROVED);
  const isTemplate = status.includes(StorageUnitStatus.TEMPLATE);

  const style = { height: '28px', padding: '0 4px' };
  return (
    <Stack
      sx={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        p: 1,
      }}
    >
      {isTemplate && (
        <Chip
          size='small'
          label='Template'
          color='info'
          sx={style}
          icon={<TemplateIcon />}
        />
      )}
      {!isApproved && !isTemplate && (
        <Chip
          size='small'
          label='Draft'
          variant='outlined'
          color='info'
          sx={{ background: 'white', color: palette.info.main, ...style }}
          icon={<EditIcon />}
        />
      )}
      {isApproved && (
        <Chip
          size='small'
          label='Approved'
          color='success'
          sx={style}
          icon={<ApprovedIcon />}
        />
      )}
    </Stack>
  );
};

const CardLockedOverlay = ({ lockedBy }: { lockedBy?: string }) => {
  const theme = useTheme();
  const tooltipText = lockedBy
    ? `View only. Locked by ${lockedBy}.`
    : 'View only. File in use by another user.';

  return (
    <>
      <Box
        id='overlay'
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme.palette.grey[900],
          opacity: 0.2,
        }}
      />
      <Tooltip title={tooltipText}>
        <LockIcon
          fontSize='large'
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 1,
            color: theme.palette.white,
          }}
        />
      </Tooltip>
    </>
  );
};

const CardThumbnail = ({
  thumbnail,
  status,
  lockState,
  lockedBy,
}: {
  thumbnail: string | null;
  status: StorageUnitStatus[];
  lockState: 'self' | 'other' | 'none';
  lockedBy?: string;
}) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [thumbnail]);

  const handleImageError = () => {
    setImageError(true);
  };

  const showPlaceholder = !thumbnail || imageError;
  const showOverlay = lockState === 'other';

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {showOverlay && <CardLockedOverlay lockedBy={lockedBy} />}
      {showPlaceholder ? (
        <ThumbnailPlaceholder />
      ) : (
        <Thumbnail src={thumbnail} onError={handleImageError} />
      )}
      <StatusChip status={status} />
    </Box>
  );
};

const StorageUnitCard = ({ storageUnit }: Props) => {
  const { openFile } = useStorageUnits();
  const { selectedIds, isSelected, toggleSelected } = useSelection();
  const isOnline = useOnlineStatus();
  const [isCached, setIsCached] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const {
    id,
    name,
    createdAt,
    updatedAt,
    type,
    access,
    status,
    exclusiveLease,
    archived,
    thumbnail,
  } = storageUnit;
  const lastModified = updatedAt ?? createdAt;
  const hasLocalFileData = hasOfflineFileData(storageUnit);

  const isLockedByOther = isLeasedByAnotherUser(exclusiveLease ?? undefined);
  const isLockedBySelf = isLeasedByCurrentUser(exclusiveLease ?? undefined);
  const lockState: 'self' | 'other' | 'none' = isLockedByOther
    ? 'other'
    : isLockedBySelf
      ? 'self'
      : 'none';
  const lockedByName = exclusiveLease?.userName;

  useEffect(() => {
    const checkCache = async () => {
      if (type !== StorageUnitType.File) {
        setIsCached(false);
        setCacheChecked(true);
        return;
      }

      if (hasLocalFileData) {
        setIsCached(true);
        setCacheChecked(true);
        return;
      }

      const cached = await storageUnitCache.getOne(id).catch(() => null);
      setIsCached(hasOfflineFileData(cached));
      setCacheChecked(true);
    };

    void checkCache();
  }, [id, type, hasLocalFileData, isOnline, storageUnit.pendingAction]);

  const isPendingOffline =
    storageUnit.pendingAction === 'create' ||
    storageUnit.pendingAction === 'update' ||
    storageUnit.pendingAction === 'archive';
  const isAvailableOffline = hasLocalFileData || isCached;
  const isCardDisabled =
    !isOnline &&
    (!cacheChecked || !isAvailableOffline) &&
    type === StorageUnitType.File &&
    !isPendingOffline;

  const checked = isSelected(id);

  useEffect(() => {
    if (isCardDisabled && checked) {
      toggleSelected(id);
    }
  }, [checked, id, isCardDisabled, toggleSelected]);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCardDisabled) {
      return;
    }
    toggleSelected(id);
  };

  const handleClick = () => {
    if (selectedIds.length > 0) {
      if (isCardDisabled) {
        return;
      }
      toggleSelected(id);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (archived || isCardDisabled) {
      return;
    }

    openFile(id);
  };

  return (
    <Card
      storageUnit={storageUnit}
      isSelected={checked}
      isDisabled={isCardDisabled}
    >
      <CardHeader>
        {!isCardDisabled && (
          <Checkbox
            checked={checked}
            onClick={handleCheckboxClick}
            icon={<RadioButtonUncheckedIcon sx={{ color: '#000' }} />}
            checkedIcon={<CheckCircleOutlinedIcon sx={{ color: '#000' }} />}
            size='small'
            disableRipple
            sx={{ padding: 0 }}
          />
        )}
        <MoreButton unit={storageUnit} disabled={isCardDisabled} />
      </CardHeader>
      <CardActionArea
        onClick={handleClick}
        disableRipple
        sx={{
          pt: 0,
          opacity: isCardDisabled ? 0.5 : 1,
          cursor: isCardDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Divider sx={{ mt: 0.5 }} />
        <Box
          sx={{
            px: 1,
            pt: 1,
          }}
        >
          <CardTitle>{splitName(type, name)}</CardTitle>
        </Box>
        <CardMedia>
          <CardThumbnail
            thumbnail={thumbnail ?? null}
            status={status}
            lockState={lockState}
            lockedBy={lockedByName}
          />
        </CardMedia>
        <Info>
          <LastEdited>
            {`Edited ${dayjs(lastModified).format('D MMM YYYY')}`}
          </LastEdited>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            {type === StorageUnitType.File && (
              <OfflineStatusIndicator
                fileId={id}
                hasLocalFileData={hasLocalFileData}
              />
            )}
            {lockState === 'self' && (
              <Tooltip title='You locked this file so no one else can edit.'>
                <LockOutlinedIcon fontSize='small' />
              </Tooltip>
            )}
            {isShared(access) && <StyledSharedIcon />}
          </Stack>
        </Info>
      </CardActionArea>
    </Card>
  );
};

export default StorageUnitCard;
